import { io, Socket } from 'socket.io-client';
import { uuidv7 } from 'uuidv7';

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'offline' | 'error';

interface ClientMessage {
    id: string;
    type: string;
    payload?: unknown;
    requestAck?: boolean;
}

interface ServerEvent {
    id?: string;
    type: string;
    payload?: unknown;
}

interface QueuedMessage extends ClientMessage {
    ackTimeoutMs?: number;
    resolve?: () => void;
    reject?: (error: Error) => void;
}

type EventHandler = (event: ServerEvent | { type: 'error'; payload: { message: string } }) => void;

const MAX_QUEUE_SIZE = 1000;
const DEFAULT_ACK_TIMEOUT = 10000;
const MAX_PROCESSED_EVENT_IDS = 1000;

class RealtimeClientManager {
    private socket: Socket | null = null;
    private status: ConnectionStatus = 'disconnected';
    private messageQueue: QueuedMessage[] = [];
    private pendingAcks = new Map<
        string,
        { resolve: () => void; reject: (error: Error) => void; timeout: NodeJS.Timeout }
    >();
    private subscribers = new Set<EventHandler>();
    private isInitialized = false;
    private pageLoadStreamId: string | null = null;
    private lastReceivedStreamId: string | null = null;
    private processedEventIds = new Set<string>();
    private hasCaughtUp = false;

    constructor() {
        if (typeof window === 'undefined') return;

        // Handle browser online/offline events
        window.addEventListener('online', () => this.handleOnline());
        window.addEventListener('offline', () => this.handleOffline());
    }

    private updateStatus(newStatus: ConnectionStatus) {
        this.status = newStatus;
        // Notify subscribers about status change
        this.notifySubscribers({ type: 'status', payload: { status: newStatus } });
    }

    private notifySubscribers(event: ServerEvent | { type: string; payload: unknown }) {
        this.subscribers.forEach((handler) => handler(event as ServerEvent));
    }

    init(pageLoadStreamId?: string | null) {
        if (this.isInitialized || typeof window === 'undefined') return;

        this.isInitialized = true;
        this.pageLoadStreamId = pageLoadStreamId ?? null;
        this.updateStatus('connecting');

        this.socket = io({
            transports: ['websocket'],
            autoConnect: true,
        });

        this.socket.on('connect', () => {
            this.updateStatus('connected');
            this.requestCatchupIfNeeded();
            this.flushQueue();
        });

        this.socket.on('disconnect', () => {
            this.updateStatus('disconnected');
        });

        this.socket.on('connect_error', (error) => {
            this.updateStatus('error');
            this.notifySubscribers({
                type: 'error',
                payload: { message: `Connection error: ${error.message}` },
            });
        });

        this.socket.on('server:event', (event: ServerEvent) => {
            this.handleServerEvent(event);
        });

        this.socket.on('server:catchup:complete', ({ count }: { count: number }) => {
            this.hasCaughtUp = true;
            this.notifySubscribers({ type: 'catchup:complete', payload: { count } });
        });

        this.socket.on('server:catchup:error', ({ message }: { error: string; message: string }) => {
            this.hasCaughtUp = true;
            this.notifySubscribers({
                type: 'error',
                payload: { message: `Catch-up error: ${message}` },
            });
        });

        this.socket.on('server:ack', ({ id }: { id: string }) => {
            const pending = this.pendingAcks.get(id);
            if (pending) {
                clearTimeout(pending.timeout);
                pending.resolve();
                this.pendingAcks.delete(id);
            }
        });
    }

    private handleOnline() {
        if (this.socket && this.status === 'offline') {
            this.updateStatus('connecting');
            this.socket.connect();
        }
    }

    private handleOffline() {
        this.updateStatus('offline');
        if (this.socket) {
            this.socket.disconnect();
        }
    }

    private requestCatchupIfNeeded() {
        // Request catch-up if we have a page load stream ID and haven't caught up yet
        if (this.pageLoadStreamId && !this.hasCaughtUp && this.socket) {
            this.socket.emit('client:catchup', { since: this.pageLoadStreamId });
        }
    }

    private handleServerEvent(event: ServerEvent) {
        // Deduplicate events
        if (event.id && this.processedEventIds.has(event.id)) {
            return; // Already processed
        }

        // Add to processed set with LRU eviction
        if (event.id) {
            this.processedEventIds.add(event.id);
            if (this.processedEventIds.size > MAX_PROCESSED_EVENT_IDS) {
                // Remove oldest entry (first in Set)
                const firstId = this.processedEventIds.values().next().value;
                if (firstId !== undefined) {
                    this.processedEventIds.delete(firstId);
                }
            }
            this.lastReceivedStreamId = event.id;
        }

        // Notify subscribers
        this.notifySubscribers(event);
    }

    private flushQueue() {
        if (!this.socket || this.status !== 'connected') return;

        while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift()!;
            this.sendMessage(message);
        }
    }

    private sendMessage(message: QueuedMessage) {
        if (!this.socket || this.status !== 'connected') {
            // Re-queue if not connected
            this.queueMessage(message);
            return;
        }

        if (message.requestAck) {
            const ackTimeoutMs = message.ackTimeoutMs || DEFAULT_ACK_TIMEOUT;
            const timeout = setTimeout(() => {
                const pending = this.pendingAcks.get(message.id);
                if (pending) {
                    pending.reject(new Error(`Ack timeout after ${ackTimeoutMs}ms`));
                    this.pendingAcks.delete(message.id);
                }
            }, ackTimeoutMs);

            if (message.resolve && message.reject) {
                this.pendingAcks.set(message.id, {
                    resolve: message.resolve,
                    reject: message.reject,
                    timeout,
                });
            }

            this.socket.emit('client:message', {
                id: message.id,
                type: message.type,
                payload: message.payload,
                requestAck: true,
            });
        } else {
            this.socket.emit('client:message', {
                id: message.id,
                type: message.type,
                payload: message.payload,
                requestAck: false,
            });
        }
    }

    private queueMessage(message: QueuedMessage) {
        if (this.messageQueue.length >= MAX_QUEUE_SIZE) {
            const dropped = this.messageQueue.shift();
            this.notifySubscribers({
                type: 'error',
                payload: { message: `Queue overflow: dropped message ${dropped?.id}` },
            });
        }
        this.messageQueue.push(message);
    }

    send(params: {
        type: string;
        payload?: unknown;
        requestAck?: boolean;
        ackTimeoutMs?: number;
    }): Promise<void> | void {
        const message: QueuedMessage = {
            id: uuidv7(),
            type: params.type,
            payload: params.payload,
            requestAck: params.requestAck,
            ackTimeoutMs: params.ackTimeoutMs,
        };

        if (params.requestAck) {
            return new Promise<void>((resolve, reject) => {
                message.resolve = resolve;
                message.reject = reject;

                if (this.status === 'connected') {
                    this.sendMessage(message);
                } else {
                    this.queueMessage(message);
                }
            });
        } else {
            if (this.status === 'connected') {
                this.sendMessage(message);
            } else {
                this.queueMessage(message);
            }
        }
    }

    subscribe(handler: EventHandler): () => void {
        this.subscribers.add(handler);
        return () => {
            this.subscribers.delete(handler);
        };
    }

    getStatus(): ConnectionStatus {
        return this.status;
    }

    // Join a Socket.IO room (for existing room-based logic)
    joinRoom(room: string) {
        if (this.socket && this.status === 'connected') {
            this.socket.emit('join', room);
        }
    }

    // Access underlying socket for existing event listeners
    get rawSocket(): Socket | null {
        return this.socket;
    }
}

// Singleton instance
let instance: RealtimeClientManager | null = null;

export function getRealtimeClient(): RealtimeClientManager {
    if (!instance) {
        instance = new RealtimeClientManager();
    }
    return instance;
}
